import { useEffect, useMemo, useState } from 'react';
import { Check, Clock3, LoaderCircle, MapPin, Store, X } from 'lucide-react';
import {
  getIfoodMerchantDetails,
  previewIfoodMerchantHours,
} from '../../../../../services/api';
import { getIfoodErrorInfo, getIfoodErrorMessage } from '../../../../../utils/ifoodError';

type ImportSelection = {
  address?: any;
  scheduleDays?: any[];
};

type Props = {
  open: boolean;
  merchantId: string;
  merchantName?: string;
  onClose: () => void;
  onApply: (selection: ImportSelection) => void;
  onReconnect: () => void;
};

const hasAddress = (address: any) => Boolean(
  address && [address.street, address.number, address.district, address.city, address.state, address.postal_code]
    .some((value) => String(value || '').trim()),
);

const addressSummary = (address: any) => [
  [address?.street, address?.number].filter(Boolean).join(', '),
  address?.district,
  [address?.city, address?.state].filter(Boolean).join(' - '),
  address?.postal_code,
].filter(Boolean).join(' · ');

const IfoodPromptImportDialog = ({ open, merchantId, merchantName, onClose, onApply, onReconnect }: Props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requiresReauthorization, setRequiresReauthorization] = useState(false);
  const [details, setDetails] = useState<any>(null);
  const [hours, setHours] = useState<any>(null);
  const [selected, setSelected] = useState({ address: true, hours: true });

  useEffect(() => {
    if (!open || !merchantId) return;
    let active = true;
    setLoading(true);
    setError('');
    setRequiresReauthorization(false);
    Promise.all([
      getIfoodMerchantDetails(merchantId),
      previewIfoodMerchantHours(merchantId),
    ])
      .then(([merchantResponse, hoursResponse]) => {
        if (!active) return;
        setDetails(merchantResponse?.details || null);
        setHours(hoursResponse || null);
        setSelected({
          address: hasAddress(merchantResponse?.details?.address),
          hours: Array.isArray(hoursResponse?.schedule_days),
        });
      })
      .catch((requestError) => {
        if (active) {
          setError(getIfoodErrorMessage(requestError, 'Não foi possível consultar os dados desta loja no iFood.'));
          setRequiresReauthorization(getIfoodErrorInfo(requestError).requiresReauthorization);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [merchantId, open]);

  const addressAvailable = hasAddress(details?.address);
  const hoursAvailable = Array.isArray(hours?.schedule_days);
  const selectedCount = useMemo(() => (
    Number(selected.address && addressAvailable) + Number(selected.hours && hoursAvailable)
  ), [addressAvailable, hoursAvailable, selected.address, selected.hours]);

  if (!open) return null;

  const handleApply = () => {
    onApply({
      ...(selected.address && addressAvailable ? { address: details.address } : {}),
      ...(selected.hours && hoursAvailable ? { scheduleDays: hours.schedule_days } : {}),
    });
    onClose();
  };

  const optionClass = (enabled: boolean, checked: boolean) => [
    'flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition',
    enabled ? 'cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/50' : 'cursor-not-allowed bg-slate-50 opacity-70',
    checked && enabled ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white',
  ].join(' ');

  return (
    <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm" role="presentation">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="ifood-import-title">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-rose-50 text-[#EA1D2C]"><Store size={22} /></div>
            <div>
              <h2 id="ifood-import-title" className="text-xl font-bold text-slate-900">Copiar dados do iFood</h2>
              <p className="mt-1 truncate text-sm text-slate-500">{merchantName || 'Loja selecionada'}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100" aria-label="Fechar"><X size={20} /></button>
        </div>

        <div className="space-y-4 p-6">
          {loading && (
            <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-sm text-slate-600">
              <LoaderCircle size={28} className="animate-spin text-[#EA1D2C]" />
              Consultando a loja no iFood…
            </div>
          )}

          {!loading && error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" role="alert">
              <p>{error}</p>
              {requiresReauthorization && (
                <button type="button" onClick={onReconnect} className="mt-3 rounded-xl bg-[#EA1D2C] px-4 py-2 text-sm font-bold text-white">
                  Refazer conexão
                </button>
              )}
            </div>
          )}

          {!loading && !error && (
            <>
              <p className="text-sm text-slate-600">Escolha quais informações devem substituir os campos atuais do AtendaBot.</p>

              <label className={optionClass(addressAvailable, selected.address)}>
                <input
                  type="checkbox"
                  checked={selected.address && addressAvailable}
                  disabled={!addressAvailable}
                  onChange={(event) => setSelected((current) => ({ ...current, address: event.target.checked }))}
                  className="mt-1 h-4 w-4 accent-emerald-600"
                />
                <MapPin size={21} className="mt-0.5 shrink-0 text-emerald-700" />
                <span className="min-w-0 flex-1">
                  <strong className="block text-sm text-slate-900">Endereço</strong>
                  <span className="mt-1 block text-xs leading-relaxed text-slate-500">
                    {addressAvailable ? addressSummary(details.address) : 'O iFood não informou o endereço desta loja.'}
                  </span>
                </span>
              </label>

              <label className={optionClass(hoursAvailable, selected.hours)}>
                <input
                  type="checkbox"
                  checked={selected.hours && hoursAvailable}
                  disabled={!hoursAvailable}
                  onChange={(event) => setSelected((current) => ({ ...current, hours: event.target.checked }))}
                  className="mt-1 h-4 w-4 accent-emerald-600"
                />
                <Clock3 size={21} className="mt-0.5 shrink-0 text-emerald-700" />
                <span className="min-w-0 flex-1">
                  <strong className="block text-sm text-slate-900">Horários de funcionamento</strong>
                  <span className="mt-1 block text-xs leading-relaxed text-slate-500">
                    {hoursAvailable
                      ? `${hours?.summary?.open_days || 0} dias com horários configurados no iFood.`
                      : 'O iFood não retornou horários para esta loja.'}
                  </span>
                </span>
              </label>

            </>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100">Cancelar</button>
          <button
            type="button"
            onClick={handleApply}
            disabled={loading || Boolean(error) || selectedCount === 0}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <Check size={17} /> Copiar selecionados
          </button>
        </div>
      </div>
    </div>
  );
};

export default IfoodPromptImportDialog;
